import { useEffect, useMemo, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { useNavigate, useSearchParams } from 'react-router-dom';

import Cookies from 'universal-cookie';

import { BookingListRow } from 'us-react-components/dist/BookingListRow/BookingListRow';
import {
  namespaces,
  useSharedTranslation,
} from 'us-react-components/dist/hooks/useSharedTranslation';
import { Pagination } from 'us-react-components/dist/Pagination/Pagination';
import {
  UserNameFilter,
  UserNameFilterOption,
} from 'us-react-components/dist/UserNameFilter/UserNameFilter';
import { Booking, BookingService } from 'us-web-services';

import { Button, ButtonType } from '../../components';
import { Storage } from '../../constants';
import { useDependencies } from '../../dependencies';


type FilterCode = 'all_active' | 'all_date' | 'completed' | 'expired_cancel' | 'rejected' | 'pending';

const FILTER_TYPES: Array<{
  code: FilterCode;
  labelKey: string;
  nameKey: string;
}> = [
  { code: 'all_active', labelKey: 'pages.bookingList.types.allActive', nameKey: 'pages.bookingList.typeNames.allActive' },
  { code: 'all_date', labelKey: 'pages.bookingList.types.allDate', nameKey: 'pages.bookingList.typeNames.allDate' },
  { code: 'completed', labelKey: 'pages.bookingList.types.completed', nameKey: 'pages.bookingList.typeNames.completed' },
  { code: 'expired_cancel', labelKey: 'pages.bookingList.types.expiredCancel', nameKey: 'pages.bookingList.typeNames.expiredCancel' },
  { code: 'rejected', labelKey: 'pages.bookingList.types.rejected', nameKey: 'pages.bookingList.typeNames.rejected' },
  { code: 'pending', labelKey: 'pages.bookingList.types.pending', nameKey: 'pages.bookingList.typeNames.pending' },
];


interface BookingWithStatus extends Booking {
  status?: Booking['status'] & {
    code?: string;
    label?: string;
    webIcon?: string;
  };
  parent?: Booking['parent'] & { active?: boolean };
  groupId?: number;
  group?: { label?: string };
}

export const BookingList = observer((): JSX.Element => {
  const { t } = useSharedTranslation(namespaces.caregiver);
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { stores } = useDependencies();
  const user = stores.user.user;

  const initialType = (searchParams.get('filterType') as FilterCode | null) ?? 'all_active';
  const [selectedType, setSelectedType] = useState<FilterCode>(initialType);
  const [bookings, setBookings] = useState<{
    data: BookingWithStatus[];
    meta?: { size?: number; pageCount?: number };
  }>({ data: [] });
  const [isFiltering, setIsFiltering] = useState(false);
  const [page, setPage] = useState<number>(Number(searchParams.get('page') || 1) || 1);
  const [totalBookings, setTotalBookings] = useState<number>(0);

  const filter = useMemo(
    () => FILTER_TYPES.find((f) => f.code === selectedType) ?? FILTER_TYPES[0],
    [selectedType],
  );

  useEffect(() => {
    document.title = 'UrbanSitter - Bookings';
  }, []);

  const loadBookings = async (nextPage: number): Promise<void> => {
    if (!user?.id) return;
    setIsFiltering(true);
    const params: Record<string, unknown> = {
      filterType: selectedType,
      page: nextPage,
      userId: user.id,
      sitterId: user.id,
    };
    const parentId = searchParams.get('parentId');
    if (parentId) params.parentId = parentId;
    try {
      const resp = (await BookingService.getByFilter({ params })).data;
      const data = (resp?.data ?? []) as BookingWithStatus[];
      setBookings({ data, meta: resp?.meta });
      if (selectedType !== 'all_date' && data.length === 0) {
        loadTotalBookings();
      }
    } catch (err) {
      console.warn('[BookingList] load failed', err);
    } finally {
      setIsFiltering(false);
    }
  };

  const loadTotalBookings = async (): Promise<void> => {
    if (!user?.id) return;
    try {
      const resp = (
        await BookingService.getByFilter({
          params: { userId: user.id, sitterId: user.id, limit: 1 },
        })
      ).data;
      setTotalBookings(resp?.meta?.size ?? 0);
    } catch {
      setTotalBookings(0);
    }
  };

  useEffect(() => {
    loadBookings(page);

  }, [user?.id, selectedType, page]);

  const setType = (code: FilterCode): void => {
    setSelectedType(code);
    setPage(1);
    setSearchParams(
      (sp) => {
        const n = new URLSearchParams(sp);
        n.set('filterType', code);
        n.set('page', '1');
        return n;
      },
      { replace: true },
    );
  };

  const onFilterUserChange = (u: UserNameFilterOption | null): void => {
    setSearchParams(
      (sp) => {
        const n = new URLSearchParams(sp);
        if (u?.id) n.set('parentId', String(u.id));
        else n.delete('parentId');
        n.set('page', '1');
        return n;
      },
      { replace: true },
    );
    setPage(1);
  };

  const setPageNum = (next: number): void => {
    setPage(next);
    setSearchParams(
      (sp) => {
        const n = new URLSearchParams(sp);
        n.set('page', String(next));
        return n;
      },
      { replace: true },
    );
  };

  const emptyFilterLabel = selectedType !== 'all_active' ? `${t(filter.nameKey)} ` : '';
  const totalPages = bookings.meta?.pageCount ?? 1;

  return (
    <div className='container py-8'>
      <h1 className='text-2xl font-semibold mb-1'>{t('pages.bookingList.heading')}</h1>
      <p className='mb-5 text-sm'>
        {t('pages.bookingList.subtitleSitter')}{' '}
        <a
          id='sitter-add-job-top'
          href='/app/sitter/booking/create'
          className='underline'
          onClick={(): void => {
            const cookies = new Cookies();
            cookies.set(Storage.BOOKING_ORIGIN, `/app${window.location.pathname}${window.location.search}`, {
              path: '/',
            });
          }}
        >
          {t('pages.bookingList.addJob')}
        </a>
      </p>

      <div className='grid grid-cols-1 sm:grid-cols-12 gap-4 mb-4'>
        <div className='sm:col-span-4 md:col-span-3'>
          <h3 className='text-base font-semibold mb-1'>
            <label htmlFor='typeSelect'>{t('pages.bookingList.typeLabel')}</label>
          </h3>
          <select
            id='typeSelect'
            className='form-control w-full border border-neutral-300 rounded px-3 py-2'
            value={selectedType}
            onChange={(e): void => setType(e.target.value as FilterCode)}
          >
            {FILTER_TYPES.map((ft) => (
              <option key={ft.code} value={ft.code}>
                {t(ft.labelKey)}
              </option>
            ))}
          </select>
        </div>
        <div className='sm:col-span-4 md:col-span-3'>
          <UserNameFilter
            kind='parent'
            viewerId={user?.id}
            label={t('common.familyLabel')}
            onSelect={onFilterUserChange}
          />
        </div>
      </div>

      <div className='grid grid-cols-1 sm:grid-cols-12 gap-4'>
        <div className='sm:col-span-9'>
          {isFiltering && (
            <h2 className='text-base'>
              <i className='fa-solid fa-spinner fa-spin mr-2' />
              {t('pages.bookingList.loading')}
            </h2>
          )}

          {!isFiltering &&
            bookings.data.map((booking) => {
              const contact = booking.parent;
              const active = contact?.active !== false;
              const cta =
                booking.status?.code === 'job_finished' && booking.paymentType === 'credit'
                  ? {
                      id: `confirm-hours-${booking.id}`,
                      label: t('pages.bookingList.confirmJobDetails'),
                      // No leading /app — this is passed to navigate(), which
                      // is already relative to the router's basename='/app'.
                      url: `/bookings/${booking.id}/confirm-hours`,
                    }
                  : null;
              return (
                <BookingListRow
                  key={booking.id}
                  bookingId={booking.id ?? ''}
                  contact={contact}
                  statusLabel={booking.status?.label}
                  statusType={booking.status?.type}
                  statusWebIcon={booking.status?.webIcon}
                  start={booking.start}
                  end={booking.end}
                  contactHref={active ? `/app/parent/${contact?.id}` : undefined}
                  viewDetailsHref={`/app/bookings/${booking.id}`}
                  ctaAction={cta && active ? (
                    <Button
                      buttonType={ButtonType.rounded}
                      id={cta.id}
                      className='btn-block w-full py-2'
                      onClick={(): void => navigate(cta.url)}
                    >
                      {cta.label}
                    </Button>
                  ) : undefined}
                  viewDetailsLabel={t('pages.bookingList.viewDetails')}
                  noLongerActiveLabel={t('pages.bookingList.noLongerActive')}
                />
              );
            })}

          {!isFiltering && bookings.data.length === 0 && (
            <div className='us-card rounded-[20px] text-center'>
              <h3 className='mb-1 font-semibold'>
                {t('pages.bookingList.emptyTitle', { filter: emptyFilterLabel, withName: '' })}
              </h3>
              {selectedType === 'all_active' && (
                <p>
                  {t('pages.bookingList.emptySitter')}{' '}
                  <a id='null-job-board' href='/app/job-board' className='underline'>
                    {t('pages.bookingList.jobBoardLink')}
                  </a>
                </p>
              )}
              {totalBookings > 0 && selectedType !== 'all_date' && (
                <Button
                  buttonType={ButtonType.tertiary}
                  id='all-bookings-with-name'
                  onClick={(): void => setType('all_date')}
                  className='cursor-pointer underline inline-block mt-3 bg-transparent border-0 p-0'
                >
                  {t('pages.bookingList.seeAll')}
                </Button>
              )}
            </div>
          )}

          {!isFiltering && (
            <Pagination
              page={page}
              pageCount={totalPages}
              firstPage={page <= 1}
              lastPage={page >= totalPages}
              onPageChange={setPageNum}
              namespace={namespaces.caregiver}
            />
          )}
        </div>
        <div className='sm:col-span-3'>
          <KeepOnPlatformSidebar />
        </div>
      </div>
    </div>
  );
});

const KeepOnPlatformSidebar = (): JSX.Element => {
  const { t } = useSharedTranslation(namespaces.caregiver);
  const cookies = new Cookies();

  const addBooking = (): void => {
    cookies.set(Storage.BOOKING_ORIGIN, `/app${window.location.pathname}${window.location.search}`, {
      path: '/',
    });
    window.location.assign('/app/sitter/booking/create');
  };

  const items = [
    {
      title: t('pages.bookingList.keepOnPlatform.peaceOfMindTitle'),
      body: t('pages.bookingList.keepOnPlatform.peaceOfMindBody'),
    },
    {
      title: t('pages.bookingList.keepOnPlatform.badgesTitle'),
      body: t('pages.bookingList.keepOnPlatform.badgesBody'),
    },
    {
      title: t('pages.bookingList.keepOnPlatform.remindersTitle'),
      body: t('pages.bookingList.keepOnPlatform.remindersBody'),
    },
  ];

  return (
    <aside className='us-card rounded-[20px]'>
      <h3 className='text-base font-semibold mb-3'>
        {t('pages.bookingList.keepOnPlatform.heading')}
      </h3>
      {items.map((item) => (
        <div key={item.title} className='mb-3'>
          <h4 className='font-semibold mb-0'>{item.title}</h4>
          <div className='text-sm text-neutral-700'>{item.body}</div>
        </div>
      ))}
      <Button
        id='sitter-add-job-side'
        buttonType={ButtonType.primary}
        className='btn-block w-full mt-2'
        onClick={addBooking}
      >
        {t('pages.bookingList.keepOnPlatform.addJobCta')}
      </Button>
    </aside>
  );
};
